import { marked } from "marked";

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  description: string;
  image?: string;
  readingTime: number;
  html: string;
  raw: string;
}

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/;

function parseFrontmatter(source: string): { data: Record<string, string>; body: string } {
  const m = source.match(FRONTMATTER_RE);
  if (!m) return { data: {}, body: source };
  const data: Record<string, string> = {};
  for (const line of m[1].split(/\r?\n/)) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    if (key) data[key] = value;
  }
  return { data, body: m[2] };
}

function estimateReadingTime(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

const RAW_POSTS = import.meta.glob("./blog/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

let cache: BlogPost[] | null = null;

export function loadPosts(): BlogPost[] {
  if (cache) return cache;
  const posts: BlogPost[] = [];
  for (const [path, raw] of Object.entries(RAW_POSTS)) {
    const slug = path.split("/").pop()!.replace(/\.md$/, "");
    const { data, body } = parseFrontmatter(raw);
    const html = marked.parse(body, { async: false }) as string;
    posts.push({
      slug,
      title: data.title ?? slug,
      date: data.date ?? "1970-01-01",
      description: data.description ?? "",
      image: data.image || undefined,
      readingTime: estimateReadingTime(body),
      html,
      raw,
    });
  }
  posts.sort((a, b) => b.date.localeCompare(a.date));
  cache = posts;
  return posts;
}

export function getPost(slug: string): BlogPost | null {
  return loadPosts().find((p) => p.slug === slug) ?? null;
}
